import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap/lib';
import { Loader } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { Dealer, DealerId } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  FieldId,
  FieldValues,
  OutreachForm,
  PreviewFormat,
} from '@upstart/harmony/lib/legos/outreachTypes';
import { NotifyFormat } from '@upstart/harmony/lib/legos/constants';
import { FieldDefaults } from '@upstart/harmony/lib/legos/constants/outreach';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import ErrorMessage from '../../components/ErrorMessage';
import LoadingOverlay from '../../components/LoadingOverlay';
import ProdigyInput from '../../components/GlobalUI/ProdigyInput';
import Switch from './inputs/Switch';
import { dealerRequest } from '../../redux/dealers/actions';
import {
  outreachFormRequest,
  updateOutreachFormRequest,
} from '../../redux/outreachForms/actions';
import {
  FieldInfo,
  getTemplateFieldListFromConfiguration,
} from '../../util/outreachFormUtils';
import OutreachFormService from '../../services/OutreachFormService';
import {
  Dealers,
  OutreachFormState,
  OutreachFormsState,
} from '../OutreachForms/types';
import './OutreachFormEditor.scss';

interface MatchParams {
  dealerId: DealerId;
  templateId: NotifyFormat;
}

interface Props extends RouteComponentProps<MatchParams> {
  actions: {
    dealerRequest: ({ dealerId }: { dealerId: DealerId }) => void;
    outreachFormRequest: ({
      dealerId,
      templateId,
    }: {
      dealerId: DealerId;
      templateId: NotifyFormat;
    }) => void;
    updateOutreachFormRequest: (outreachForm: OutreachForm) => void;
  };
  dealer?: Dealer;
  outreachForm: OutreachFormState;
  error?: string | null;
  isFetching?: boolean;
}

const OutreachFormEditor: FC<Props> = ({
  actions,
  dealer,
  outreachForm: outreachFormProp,
  error,
  isFetching,
  history,
  match,
}: Props) => {
  const { dealerId, templateId } = match.params;

  const [outreachForm, setOutreachForm] = useState(cloneDeep(outreachFormProp));

  /* Keep track of the last loaded OutreachForm, so that we can disable the Save
   * button if no changes have been made. */
  const [outreachFormLoaded, setOutreachFormLoaded] = useState(
    cloneDeep(outreachFormProp),
  );

  /* Local state to keep track of preview images. */
  const [previewSubject, setPreviewSubject] = useState('(Loading...)');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewFetching, setIsPreviewFetching] = useState(false);
  const [previewError, setPreviewError] = useState<any>(null);

  /* Keep track of the incoming OutreachForm prop, usually updated by Redux
   * state changes, in order to overwrite local state in response.  Note that
   * changes to dealerId and templateId will eventually trickle down, causing
   * OutreachForm to become updated too, but in the event of backend API
   * request failures, we'll specify those variables as dependencies too. */
  useEffect((): void => {
    setOutreachForm(cloneDeep(outreachFormProp));
    setOutreachFormLoaded(cloneDeep(outreachFormProp));
    setPreviewUrl(null);
    setIsPreviewFetching(false);
    setPreviewError(null);
  }, [dealerId, templateId, outreachFormProp]);

  /* Refreshing "Dealer" is necessary to maintain state when user does
   * something like "Open in New Tab" etc., otherwise DealerNav and such will
   * be rendered with dealer "null". */
  useEffect(() => {
    actions.dealerRequest({ dealerId });
  }, [actions, dealerId]);

  // Whenever key props change, issue a new API request for the relevant data.
  useEffect((): void => {
    actions.outreachFormRequest({ dealerId, templateId });
  }, [actions, dealerId, templateId]);

  const orderedTemplateFields: FieldInfo[] = useMemo(
    () => getTemplateFieldListFromConfiguration(templateId),
    [templateId],
  );

  const templateFieldDefaults: FieldValues = useMemo(
    () => FieldDefaults[templateId] || {},
    [templateId],
  );

  const isPreviewFetchEnabled: boolean = useMemo(
    () =>
      !!(
        dealerId &&
        dealerId === outreachForm.dealerId &&
        templateId &&
        templateId === outreachForm.templateId &&
        isPreviewFetching !== true
      ),
    [
      dealerId,
      templateId,
      outreachForm.dealerId,
      outreachForm.templateId,
      isPreviewFetching,
    ],
  );

  const isResetAllEnabled: boolean = useMemo(
    () =>
      Object.values(orderedTemplateFields).some(
        (field) => outreachForm.values?.[field.fieldId] !== field.defaultValue,
      ),
    [outreachForm, orderedTemplateFields],
  );

  const isSaveEnabled: boolean = useMemo(
    () =>
      outreachForm.enabled !== outreachFormLoaded.enabled ||
      Object.values(orderedTemplateFields).some(
        (field: FieldInfo) =>
          outreachForm.values?.[field.fieldId] !==
          outreachFormLoaded.values?.[field.fieldId],
      ),
    [
      outreachForm.enabled,
      outreachForm.values,
      outreachFormLoaded.enabled,
      outreachFormLoaded.values,
      orderedTemplateFields,
    ],
  );

  const onTemplateCancel = useCallback(
    () => history.push(`/${dealerId}/outreach`),
    [dealerId, history],
  );

  const onTemplateEnable = useCallback(
    (enabled: boolean) => {
      const newOutreachForm = cloneDeep(outreachForm);
      newOutreachForm.enabled = enabled;
      setOutreachForm(newOutreachForm);
    },
    [outreachForm],
  );

  const onTemplateFieldReset = useCallback(
    (fieldId: FieldId) => {
      const newOutreachForm = cloneDeep(outreachForm);
      if (!newOutreachForm.values) {
        newOutreachForm.values = {};
      }
      newOutreachForm.values[fieldId] = templateFieldDefaults[fieldId];
      setOutreachForm(newOutreachForm);
    },
    [outreachForm, templateFieldDefaults],
  );

  const onTemplateFieldSet = useCallback(
    (fieldId: FieldId, value: string) => {
      const newOutreachForm = cloneDeep(outreachForm);
      if (!newOutreachForm.values) {
        newOutreachForm.values = {};
      }
      newOutreachForm.values[fieldId] = value;
      setOutreachForm(newOutreachForm);
    },
    [outreachForm],
  );

  const onTemplateFieldsReset = useCallback(() => {
    const newOutreachForm = cloneDeep(outreachForm);
    if (!newOutreachForm.values) {
      newOutreachForm.values = {};
    }
    Object.entries(templateFieldDefaults).forEach(
      ([fieldId, defaultValue]: [string, string | undefined]) => {
        // This check should always equal true, but is needed for TypeScript.
        if (newOutreachForm.values) {
          newOutreachForm.values[fieldId as FieldId] = defaultValue;
        }
      },
    );
    setOutreachForm(newOutreachForm);
  }, [outreachForm, templateFieldDefaults]);

  const previewOutreachFormRequest = useCallback(
    async (values?: object) => {
      if (isPreviewFetchEnabled) {
        try {
          setIsPreviewFetching(true);
          setPreviewSubject('(Loading...)');

          const payload = await OutreachFormService.previewOutreachForm(
            { dealerId, templateId, values },
            PreviewFormat.JPG,
          );

          let { url } = payload;
          if (url) {
            const delim = url.match(/[?]/) ? '&' : '?';
            /* Update the received URL with a timestamp, refreshing the image
             * within the React component and bypassing any caching. */
            url = `${url}${delim}v=${Date.now()}`;
          }

          setIsPreviewFetching(false);
          setPreviewError(null);
          setPreviewUrl(url);
          setPreviewSubject(payload.subject);
        } catch (err) {
          setIsPreviewFetching(false);
          setPreviewSubject('(Error loading preview)');
          setPreviewError(err);
          setPreviewUrl(null);
        }
      }
    },
    [dealerId, isPreviewFetchEnabled, templateId],
  );

  const onTemplatePreview = useCallback(() => {
    if (isPreviewFetchEnabled) {
      previewOutreachFormRequest(outreachForm.values);
    }
  }, [isPreviewFetchEnabled, outreachForm, previewOutreachFormRequest]);

  const onTemplatePreviewOriginal = useCallback(() => {
    if (isPreviewFetchEnabled) {
      previewOutreachFormRequest();
    }
  }, [isPreviewFetchEnabled, previewOutreachFormRequest]);

  const onTemplateSave = useCallback(() => {
    actions.updateOutreachFormRequest(outreachForm as OutreachForm);
  }, [actions, outreachForm]);

  useEffect(() => {
    // Retrieve the preview image, if necessary.
    if (isPreviewFetchEnabled && !(previewUrl || previewError)) {
      onTemplatePreview();
    }
  }, [isPreviewFetchEnabled, previewUrl, previewError, onTemplatePreview]);

  if (!dealerId || !templateId) {
    return null;
  }

  return (
    <SidebarContainer
      title={dealer?.name || 'Outreach Forms'}
      logo={dealer?.logo}
    >
      <div className="dealers-table">
        <DealerNav />
        <div className="dealer-editor-container tabbed-page-container">
          {error && <ErrorMessage error={error} />}
          <LoadingOverlay active={isFetching}>
            <div className="dealer-editor-container outreach-form-container">
              <Grid fluid>
                <Row>
                  <div className="form-options">
                    <Switch
                      name="enabled"
                      label="Enabled:"
                      formData={{ enabled: outreachForm.enabled }}
                      onChange={() => onTemplateEnable(!outreachForm.enabled)}
                    />
                  </div>
                </Row>
                <Row>
                  <Col sm={6} className="col-preview-column">
                    <div>
                      <div className="preview-subject">
                        <h4>Subject:</h4>
                        <span>
                          {previewSubject || '(No email subject available)'}
                        </span>
                      </div>
                      <div className="preview-ui">
                        <div className="preview-image">
                          {previewError && (
                            <ErrorMessage error={previewError} />
                          )}
                          <Loader
                            active={isPreviewFetching}
                            size="large"
                            inline="centered"
                          />
                          {!isPreviewFetching && previewUrl && (
                            <img
                              alt="Template preview"
                              src={previewUrl ?? ''}
                            />
                          )}
                        </div>
                        <div className="preview-actions">
                          <button
                            className="btn btn-default"
                            disabled={!isPreviewFetchEnabled}
                            onClick={onTemplatePreviewOriginal}
                          >
                            See Original
                          </button>
                          <button
                            className="btn btn-primary"
                            disabled={!isPreviewFetchEnabled}
                            onClick={onTemplatePreview}
                          >
                            Preview Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6} className="col-template-fields-column">
                    <div className="template-fields-actions">
                      <h4>Email Content</h4>
                      <Button
                        className="pt-5 btn btn-primary no-border"
                        disabled={!isResetAllEnabled}
                        onClick={onTemplateFieldsReset}
                      >
                        Reset All
                      </Button>
                    </div>
                    {Object.values(orderedTemplateFields).map(
                      ({ fieldId, name }) => (
                        <Fragment key={fieldId}>
                          <ProdigyInput
                            value={outreachForm.values?.[fieldId] ?? ''}
                            label={name || 'Unknown field'}
                            required={false}
                            disabled={false}
                            placeholder=""
                            onChangeHandler={(newValue) =>
                              onTemplateFieldSet(fieldId, newValue)
                            }
                          />
                          <Button
                            className="pt-5 no-border"
                            disabled={
                              outreachForm.values?.[fieldId] ===
                              templateFieldDefaults[fieldId]
                            }
                            onClick={() => onTemplateFieldReset(fieldId)}
                          >
                            <i className="glyphicon glyphicon-refresh" />
                          </Button>
                        </Fragment>
                      ),
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} className="col-form-actions">
                    <div className="form-actions">
                      <button
                        className="btn btn-default"
                        onClick={onTemplateCancel}
                      >
                        {isSaveEnabled ? 'Cancel' : 'Back'}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={onTemplateSave}
                        disabled={!isSaveEnabled}
                      >
                        Save
                      </button>
                    </div>
                  </Col>
                </Row>
              </Grid>
            </div>
          </LoadingOverlay>
        </div>
      </div>
    </SidebarContainer>
  );
};

const mapStateToProps = ({
  dealers,
  outreachForms,
}: {
  dealers?: Dealers;
  outreachForms: OutreachFormsState;
}) => ({
  dealer: dealers?.dealer,
  outreachForm: outreachForms.outreachForm,
  error: outreachForms?.error,
  isFetching: dealers?.fetching || outreachForms?.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    { dealerRequest, outreachFormRequest, updateOutreachFormRequest },
    dispatch,
  ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OutreachFormEditor),
);
